/**
 * Notification write hooks.
 *
 * Centralises the mark-as-read mutations and invalidates the shared
 * notification cache (lists + unread count) on success.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { markAllNotificationsRead, markNotificationRead } from "@/data/raw";
import { keys } from "@/data/keys";

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await markAllNotificationsRead(userId);
      if (error) throw error;
      return { userId };
    },
    onSuccess: ({ userId }) => {
      qc.invalidateQueries({ queryKey: keys.notifications.list(userId) });
      qc.invalidateQueries({ queryKey: keys.notifications.unreadCount(userId) });
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await markNotificationRead(id, userId);
      if (error) throw error;
      return { id, userId };
    },
    onSuccess: ({ userId }) => {
      qc.invalidateQueries({ queryKey: keys.notifications.list(userId) });
      qc.invalidateQueries({ queryKey: keys.notifications.unreadCount(userId) });
    },
  });
}
